import { SingleDateInput } from '@pandiafi/design-system'
import { Field, FieldProps } from 'formik'
import { useTranslation } from 'react-i18next'

import FormFieldWrapper from 'components/FormField'
import { FormFieldDescription } from 'components/FormField/FormFieldDescription'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { FormFieldInfoTooltip } from 'components/FormFieldInfoTooltip/FormFieldInfoTooltip'
import { renderDate, safeISODateFromLocaleDate } from 'utils'
import { getAllowedMonthEndDates } from 'utils/terminationDateUtils'
import { FormValues } from 'views/TerminateContract/TerminateForm/initialValues'

type EndDateFieldInputProps = FieldProps<FormValues['endDate']> & {
  monthEndOnly: boolean
}

const EndDateFieldInput = ({
  field,
  meta,
  form,
  monthEndOnly,
}: EndDateFieldInputProps) => {
  const { t } = useTranslation(['common', 'terminateContract'])

  const allowedDates = monthEndOnly
    ? getAllowedMonthEndDates(
        form.values.firstPossibleEndDate,
        form.values.lastPossibleEndDate
      )
    : undefined

  return (
    <FormFieldWrapper
      name={field.name}
      label={t('terminateContract:labels.contractEndDate')}
      className="mt-8"
      meta={meta}
      labelBold
      labelAddon={
        <FormFieldInfoTooltip
          text={t('terminateContract:texts.contractEndDateTooltip')}
        />
      }
      description={
        monthEndOnly && (
          <FormFieldDescription text={t('common:datePicker.monthEndOnly')} />
        )
      }
    >
      <SingleDateInput
        {...field}
        value={renderDate(field.value)}
        onChange={(value) => {
          const date = safeISODateFromLocaleDate(value)
          if (date) {
            form.setFieldValue(field.name, date, true)
          } else {
            form.setFieldValue(field.name, value, false)
          }
        }}
        disabledAfter={form.values.lastPossibleEndDate.toJSDate()}
        disabledBefore={form.values.firstPossibleEndDate.toJSDate()}
        allowedDates={allowedDates}
        placeholder={t('common:placeholders.date')}
        datePickerPosition="bottomLeft"
      />
    </FormFieldWrapper>
  )
}

export const EndDateField = () => {
  const moduleConfig = useModuleConfig()
  const monthEndOnly =
    moduleConfig.terminateContract?.endDateLastDayOfMonth ?? true

  return (
    <Field name="endDate">
      {(props: FieldProps<FormValues['endDate']>) => (
        <EndDateFieldInput {...props} monthEndOnly={monthEndOnly} />
      )}
    </Field>
  )
}
